import { useEffect, useState } from 'react';
import c from '../../constant';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	Button,
	Box,
	Flex,
	Text,
	useToast,
	Avatar,
	Wrap,
	FormLabel,
	WrapItem,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import 'moment/locale/en-gb';
import DateRangeFormField from '../../components/form/DateRangeFormField';
import ExportExcelTrainingRecord from './excel';
import { gql, useLazyQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusSquare, faPlusSquare, faSync } from '@fortawesome/free-solid-svg-icons';
import useStore from '../../store';
import LabelDetail from '../../components/LabelDetail';
import SelectUserAllModal from '../../components/Modal/SelectUserShowAllModal';

const QUERY_EXPORT_TRAINING_RECORDS = gql`
	query exportTrainingRecords($input: ExportTrainingRecordInput!) {
		exportTrainingRecords(input: $input) {
			id
			no
			type
			locationType
			courseTitle
			means
			location
			locationType
			organiser
			createdBy
			createdAt
			dateStart
			dateEnd
			bioListing
			user {
				id
				username
				fullName
				fullNameKhmer
				department
				office
				position
			}
		}
	}
`;

const filterSchema = Yup.object().shape({
	users: Yup.array().nullable(),
	createdDate: Yup.string().nullable(),
});

const LABEL_WIDTH = '169px';

function TrainingRecordExport({ isOpen, onClose }) {
	moment.locale('en-gb');
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const toast = useToast();
	const [clearKey, setClearKey] = useState(0);
	const [officials, setOfficials] = useState([]);
	const [openSelectUserAllModel, setOpenSelectUserAllModel] = useState(false);
	const [exportTrainingRecords, { data, loading }] = useLazyQuery(QUERY_EXPORT_TRAINING_RECORDS, {
		fetchPolicy: 'network-only',
	});

	useEffect(() => {
		if (data?.exportTrainingRecords?.length === 0) {
			toast({
				title: t('Training record does not exist'),
				status: 'warning',
				isClosable: true,
				position: 'top-right',
				duration: 3000,
			});
		}
	}, [data]);

	const canExportAll = user?.role === 'admin' || user?.canExportTrainingCourse === true;

	function callbackExport(values) {
		let dateStart = null;
		let dateEnd = null;

		if (values?.createdDate) {
			const tmpDate = JSON.parse(values.createdDate);

			if (tmpDate?.from && tmpDate?.to) {
				const inputFrom = {
					day: tmpDate?.from?.day,
					month: tmpDate?.from?.month - 1,
					year: tmpDate?.from?.year,
				};

				const inputTo = {
					day: tmpDate?.to?.day,
					month: tmpDate?.to?.month - 1,
					year: tmpDate?.to?.year,
				};

				dateStart = moment(inputFrom).startOf('day').toDate();
				dateEnd = moment(inputTo).endOf('day').toDate();
			}
		}

		if (!dateStart || !dateEnd) {
			dateStart = moment().startOf('month').toDate();
			dateEnd = moment().toDate();
		}

		const users = canExportAll ? (values?.users?.length > 0 ? values.users : []) : user?.id ? [user.id] : [];

		exportTrainingRecords({
			variables: {
				input: {
					users,
					dateStart: dateStart && dateEnd ? dateStart : null,
					dateEnd: dateStart && dateEnd ? dateEnd : null,
				},
			},
		});
	}

	return (
		<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius}>
				<ModalHeader>{t('Export Training Course')}</ModalHeader>
				<ModalCloseButton />

				<ModalBody>
					<Formik
						initialValues={{
							users: [],
							createdDate: '',
						}}
						validationSchema={filterSchema}
						onSubmit={callbackExport}
					>
						{(props) => (
							<Box pb="4">
								<Form>
									{canExportAll && (
										<>
											<FormLabel>{t('Officials')}</FormLabel>

											<Flex mb={3}>
												<LabelDetail
													mb={1}
													mt={0}
													labelWidth={LABEL_WIDTH}
													childDetail={
														<Wrap>
															{officials?.map((item, index) => {
																return (
																	<Flex
																		direction="column"
																		justifyContent="center"
																		alignItems="center"
																		key={item?.id || 'u-' + index}
																		mr={2}
																	>
																		<WrapItem className="avatarWraper">
																			<FontAwesomeIcon
																				onClick={() => {
																					const newOfficials = officials.filter((u) => u?.id !== item?.id);

																					setOfficials(newOfficials);

																					props.setFieldValue(
																						'users',
																						newOfficials.map((u) => u?.id),
																					);

																					toast({
																						title: t('You have removed a user'),
																						status: 'warning',
																						isClosable: true,
																						position: 'top-right',
																					});
																				}}
																				className="remove"
																				icon={faMinusSquare}
																				style={{
																					fontSize: 18,
																					display: 'none',
																					position: 'absolute',
																					zIndex: '100',
																					marginLeft: 32,
																					color: 'red',
																					backgroundColor: 'white',
																					padding: 2,
																					cursor: 'pointer',
																				}}
																			/>

																			<Avatar
																				name={item?.fullNameKhmer || item?.fullName}
																				src={item?.avatar?.icon ? c.baseFilePath + item?.avatar?.icon : null}
																			/>
																		</WrapItem>

																		<Box mt={1}>
																			<Text textAlign="center">
																				{lang === 'kh' ? item?.fullNameKhmer : item?.fullName}
																			</Text>
																		</Box>
																	</Flex>
																);
															})}

															<Flex>
																<Button
																	className="hidden-print"
																	type="button"
																	onClick={() => setOpenSelectUserAllModel(true)}
																	leftIcon={
																		<FontAwesomeIcon
																			icon={officials?.length ? faSync : faPlusSquare}
																			style={{ fontSize: 16 }}
																		/>
																	}
																	colorScheme="blue"
																	variant="outline"
																>
																	{officials?.length > 0 ? t('Change') : t('Select Officials')}
																</Button>
															</Flex>
														</Wrap>
													}
												/>
											</Flex>

											{openSelectUserAllModel && (
												<SelectUserAllModal
													isOpen={openSelectUserAllModel}
													onClose={() => setOpenSelectUserAllModel(false)}
													selectedUsers={officials}
													callback={(selectedUsers) => {
														let newOfficials = [];

														if (Array.isArray(selectedUsers)) {
															newOfficials = selectedUsers;
														} else if (Array.isArray(selectedUsers?.users)) {
															newOfficials = selectedUsers.users;
														} else if (Array.isArray(selectedUsers?.selectedUsers)) {
															newOfficials = selectedUsers.selectedUsers;
														} else if (selectedUsers?.id) {
															newOfficials = [selectedUsers];
														}

														setOfficials(newOfficials);

														props.setFieldValue(
															'users',
															newOfficials.map((u) => u?.id),
														);

														setOpenSelectUserAllModel(false);
													}}
												/>
											)}
										</>
									)}

									<center>
										<DateRangeFormField
											key={`createdDate-${clearKey}`}
											{...props}
											label="Created Date"
											name="createdDate"
											initialDate={{ fromDate: '', toDate: '' }}
										/>{' '}
									</center>

									<Flex mt="32px" justifyContent="flex-end">
										<Button
											variant="solid"
											size="md"
											h="42px"
											w="128px"
											type="submit"
											borderRadius={c.borderRadius}
											color="#3498db"
											isLoading={loading}
										>
											{t('Get Data')}
										</Button>

										<Button
											type="button"
											size="md"
											h="42px"
											ml={4}
											w="128px"
											onClick={() => {
												props.resetForm();
												setOfficials([]);
												setClearKey((prev) => prev + 1);
											}}
											variant="ghost"
										>
											{t('Clear')}
										</Button>
									</Flex>
								</Form>

								{data?.exportTrainingRecords?.length > 0 && (
									<Box mt="24px" borderTop="1px solid #e2e8f0" pt="16px">
										<Flex alignItems="center" justifyContent="space-between">
											<Text>
												{t('Total Rows')}: {data?.exportTrainingRecords?.length}
											</Text>
											<ExportExcelTrainingRecord records={data?.exportTrainingRecords} />
										</Flex>
									</Box>
								)}
							</Box>
						)}
					</Formik>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default TrainingRecordExport;
